import { FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  BankOutlined,
  DollarOutlined,
  SafetyCertificateOutlined,
  BookOutlined,
} from '@ant-design/icons';
import SidebarPage from '../../../layout/SidebarPage/SidebarPage';
import PageTitle from '../../../layout/PageTitle/PageTitle';
import { useGet } from '../../../hooks/useApi';
import { PrimaryGuardian } from '../../../models/admin/family-info.model';
import { useContextValue } from '../../../Context';

const { Title } = Typography;

const FamilyInfo: FC = () => {
  const { primaryId } = useParams<{ primaryId: string }>();
  const navigate = useNavigate();
  const { state } = useContextValue();

  const api = useGet<PrimaryGuardian>(
    `/v1/auth/admin/family-info/${primaryId}`,
    {
      enabled: !!primaryId,
    }
  );

  const OnError = () => (
    <Alert
      message="Error while loading family information"
      description="Unable to load the family information. Please try again."
      type="error"
      showIcon
      action={
        <Button size="small" type="primary" onClick={() => api.refetch()}>
          Reload
        </Button>
      }
    />
  );

  if (api.isLoading) {
    return (
      <SidebarPage>
        <PageTitle>Loading...</PageTitle>
        <Skeleton active />
      </SidebarPage>
    );
  }

  if (api.isError) {
    return (
      <SidebarPage>
        <PageTitle>Family Info</PageTitle>
        <OnError />
      </SidebarPage>
    );
  }

  const data = api.data;
  // Backend returns PrimaryGuardian at root level with nested Students, User, AccountNotes
  const primary = data; // Root level is the primary guardian
  const students = (data as any)?.Students || []; // Capitalized from Sequelize
  const user = (data as any)?.User || null; // Capitalized from Sequelize
  const stickyNotes = (data as any)?.AccountNotes || []; // Capitalized from Sequelize

  // TODO: Replace with actual RBAC check when backend provides user roles
  // Current limitation: Backend does not return user roles in authentication context
  // Temporary behavior: Show User Info button to all authenticated users
  // Expected behavior: Check if user has 'Web Admin' role from state.user.roles
  const isWebAdmin = state.user?.id !== undefined;

  return (
    <SidebarPage>
      <PageTitle>
        {primary
          ? `${primary.firstName} ${primary.lastName} - Family Info`
          : 'Family Info'}
      </PageTitle>

      {/* Sticky Notes (Orange Callout) */}
      {stickyNotes.length > 0 && (
        <Alert
          message="Account Notes (Sticky)"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {stickyNotes.map((note: any) => (
                <li key={note.id}>{note.note}</li>
              ))}
            </ul>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Quick Actions */}
      <Space style={{ marginBottom: 24 }} wrap>
        <Button
          type="primary"
          icon={<DollarOutlined />}
          onClick={() => navigate(`/admin/ledger/${primaryId}`)}
        >
          Family Ledger
        </Button>
        {isWebAdmin && user && (
          <Button
            icon={<UserOutlined />}
            onClick={() => navigate(`/admin/users/${user.id}`)}
          >
            User Info
          </Button>
        )}
      </Space>

      {/* Primary Guardian Information */}
      {primary && (
        <Card
          title={
            <Space>
              <UserOutlined />
              <span>Primary Guardian Information</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Name">
              {primary.firstName} {primary.lastName}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              <Space>
                <MailOutlined />
                <a href={`mailto:${primary.email}`}>{primary.email}</a>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Cell Phone">
              <Space>
                <PhoneOutlined />
                {primary.phoneCell || 'N/A'}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Home Phone">
              <Space>
                <PhoneOutlined />
                {primary.phoneHome || 'N/A'}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Work Phone">
              <Space>
                <PhoneOutlined />
                {primary.phoneWork || 'N/A'}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Address" span={2}>
              <Space>
                <HomeOutlined />
                {primary.addressStreet && primary.addressCity
                  ? (() => {
                      const parts = [
                        primary.addressStreet,
                        primary.addressCity,
                        [primary.addressState, primary.addressZip]
                          .filter(Boolean)
                          .join(' '),
                      ].filter(Boolean);
                      return parts.join(', ');
                    })()
                  : 'N/A'}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Employer">
              <Space>
                <BankOutlined />
                {primary.employerName || 'N/A'}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Employer Phone">
              <Space>
                <PhoneOutlined />
                {primary.employerPhone || 'N/A'}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Students */}
      <Title level={4}>Students</Title>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {students.length === 0 ? (
          <Alert message="No students found for this family" type="info" />
        ) : (
          students.map((student: any) => (
            <Card
              key={student.id}
              title={`${student.firstName} ${student.lastName}`}
              extra={
                <Space>
                  <Button
                    type="link"
                    icon={<UserOutlined />}
                    onClick={() => navigate(`/admin/students/${student.id}`)}
                  >
                    Student Info
                  </Button>
                  <Button
                    type="link"
                    icon={<SafetyCertificateOutlined />}
                    onClick={() =>
                      navigate(`/admin/students/${student.id}/emergency-sheet`)
                    }
                  >
                    Print Emergency Sheet
                  </Button>
                  <Button
                    type="link"
                    icon={<BookOutlined />}
                    onClick={() =>
                      navigate(`/admin/students/${student.id}/packets`)
                    }
                  >
                    Enrollment Packets
                  </Button>
                </Space>
              }
            >
              <Descriptions column={1}>
                <Descriptions.Item label="Grade">
                  {student.gradeId ? `Grade ${student.gradeId}` : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="School">
                  {student.schoolId ? `School ID: ${student.schoolId}` : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ))
        )}
      </Space>
    </SidebarPage>
  );
};

export default FamilyInfo;
